import { useState, type FormEvent } from "react";
import { useMutation } from "@tanstack/react-query";
import { CheckCircle2, Play, Wrench } from "lucide-react";

import { transportService, type Trip, type Vehicle, type VehicleMaintenance } from "../../services/transportService";
import { apiErrorMessage } from "../../utils/apiError";
import { daysFromTodayIso, todayIso } from "../../utils/dates";
import { Field, FormSection } from "../FormLayout";
import { FormModal } from "../FormModal";
import { ClientRegister, StatusDot, type RegisterColumn } from "../register/ClientRegister";
import { ActionPanel, DetailsModal } from "../register/DetailsModal";
import { Notice, type NoticeState } from "../register/RecordParts";
import { AssistedText, CancelPanel, errorLine } from "./TransportParts";
import { MAINTENANCE_TYPES, PRIORITIES, day, isOpenJob, labelFrom, maintenanceConfig, maintenanceStatus, money, priorityDot } from "./transportLogic";

function RequestForm({ vehicles, busyVehicles, onClose, onDone }: { vehicles: Vehicle[]; busyVehicles: Set<number>; onClose: () => void; onDone: (row: VehicleMaintenance) => void }) {
  const usable = vehicles.filter((row) => row.is_active && row.status !== "retired" && row.status !== "sold");
  const [vehicleId, setVehicleId] = useState(usable.length === 1 ? String(usable[0].id) : "");
  const [kind, setKind] = useState("repair");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const save = useMutation({ mutationFn: transportService.requestMaintenance, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    save.mutate({
      vehicle: Number(vehicleId),
      maintenance_type: kind,
      priority: String(data.get("priority")),
      title: title.trim(),
      description: description.trim(),
      estimated_cost: String(data.get("estimated_cost") ?? "").trim() || null,
    });
  };
  return (
    <FormModal
      id="maintenance-request"
      title="Ask for a repair or service"
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The request could not be sent.") : undefined}
      saveLabel="Send the request"
      note="The transport manager books it into the workshop."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The vehicle" divided={false}>
        <Field label="Vehicle" hint={busyVehicles.has(Number(vehicleId)) ? "This vehicle already has an open request; add to that one instead." : undefined}>
          <select required value={vehicleId} onChange={(event) => setVehicleId(event.target.value)} className="input mt-1">
            <option value="">Choose a vehicle</option>
            {usable.map((row) => <option key={row.id} value={row.id}>{row.name} · {row.registration_number}</option>)}
          </select>
        </Field>
        <Field label="Kind of work">
          <select value={kind} onChange={(event) => setKind(event.target.value)} className="input mt-1">
            {MAINTENANCE_TYPES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        <Field label="Priority" hint="Urgent means it is not safe to drive.">
          <select name="priority" defaultValue="medium" className="input mt-1">
            {PRIORITIES.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
          </select>
        </Field>
        <Field label="Estimated cost (UGX)" optional>
          <input type="number" name="estimated_cost" min={0} step="any" className="input mt-1 tabular-nums" />
        </Field>
      </FormSection>
      <FormSection title="What is wrong">
        <Field label="Title" className="sm:col-span-2" hint="e.g. Brakes squeal when stopping.">
          <input required value={title} onChange={(event) => setTitle(event.target.value)} maxLength={200} className="input mt-1" />
        </Field>
        <Field label="Describe it" as="div" className="sm:col-span-2">
          <AssistedText
            name="description"
            value={description}
            onChange={setDescription}
            context={{ key: "transport.note", params: { kind: "maintenance", vehicle: vehicleId || undefined, title, maintenance_type: kind } }}
            placeholder="When it happens, what you hear or see, since when."
            draftHint="Jot what you noticed; it writes it up for the mechanic without guessing the fault."
          />
        </Field>
      </FormSection>
    </FormModal>
  );
}

function CompleteForm({ job, onClose, onDone }: { job: VehicleMaintenance; onClose: () => void; onDone: (row: VehicleMaintenance) => void }) {
  const [checked, setChecked] = useState(false);
  const save = useMutation({ mutationFn: transportService.completeMaintenance, onSuccess: onDone });
  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const text = (name: string) => String(data.get(name) ?? "").trim() || null;
    save.mutate({
      id: job.id,
      work_performed: text("work"),
      parts_used: text("parts"),
      labor_hours: text("hours"),
      actual_cost: text("cost"),
      quality_check: checked,
      quality_notes: text("quality_notes"),
      next_maintenance_due: text("next_due"),
    });
  };
  return (
    <FormModal
      id="maintenance-complete"
      title={`Back from the workshop · ${job.vehicle_name}`}
      busy={save.isPending}
      error={save.isError ? apiErrorMessage(save.error, "The job could not be closed.") : undefined}
      saveLabel="Close the job"
      note="The vehicle goes back on the road."
      onClose={onClose}
      onSubmit={submit}
    >
      <FormSection title="The work" divided={false}>
        <Field label="Work done" className="sm:col-span-2">
          <textarea required name="work" rows={3} className="input mt-1" />
        </Field>
        <Field label="Parts used" optional className="sm:col-span-2">
          <textarea name="parts" rows={2} className="input mt-1" />
        </Field>
        <Field label="Cost (UGX)" optional hint={job.estimated_cost ? `Estimated ${money(job.estimated_cost)}.` : undefined}>
          <input type="number" name="cost" min={0} step="any" className="input mt-1 tabular-nums" />
        </Field>
        <Field label="Hours of labour" optional>
          <input type="number" name="hours" min={0} step="0.5" className="input mt-1 tabular-nums" />
        </Field>
      </FormSection>
      <FormSection title="Before it goes back on the road">
        <label className="flex items-center gap-2 text-sm font-medium text-gray-700 sm:col-span-2">
          <input type="checkbox" checked={checked} onChange={(event) => setChecked(event.target.checked)} className="h-4 w-4 rounded border-gray-300" />
          Checked and safe to drive
        </label>
        <Field label="Check notes" optional>
          <input name="quality_notes" className="input mt-1" />
        </Field>
        <Field label="Next service due" optional>
          <input type="date" name="next_due" min={daysFromTodayIso(1)} className="input mt-1" />
        </Field>
      </FormSection>
    </FormModal>
  );
}

/** Repairs and services, from the driver's request to the vehicle going back on the road. */
export function MaintenancePanel({ jobs, vehicles, trips, isLoading, isError, onRetry, canManage, onChanged , initialView}: {
  /** A register view to open on, e.g. "papers" from the dashboard. */
  initialView?: string;
  jobs?: VehicleMaintenance[];
  vehicles: Vehicle[];
  trips: Trip[];
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
  canManage: boolean;
  onChanged: () => void;
}) {
  const today = todayIso();
  const [notice, setNotice] = useState<NoticeState>(null);
  const [viewing, setViewing] = useState<number | null>(null);
  const [requesting, setRequesting] = useState(false);
  const [completing, setCompleting] = useState(false);
  const done = (text: string, close = false) => {
    setNotice({ tone: "success", text });
    if (close) setViewing(null);
    onChanged();
  };
  const start = useMutation({ mutationFn: transportService.startMaintenance, onSuccess: (row) => done(`${row.vehicle_name} is in the workshop; it cannot be given trips until the job is closed.`) });
  const cancel = useMutation({ mutationFn: transportService.cancelMaintenance, onSuccess: (row) => done(`The job on ${row.vehicle_name} was cancelled.`, true) });
  const busyVehicles = new Set((jobs ?? []).filter(isOpenJob).map((row) => row.vehicle));

  const columns: RegisterColumn<VehicleMaintenance>[] = [
    { header: "Asked for", cell: (row) => day(row.requested_date), exportValue: (row) => day(row.requested_date), className: "whitespace-nowrap tabular-nums text-gray-700" },
    {
      header: "Vehicle",
      cell: (row) => (
        <span>
          <span className="font-medium text-gray-900">{row.vehicle_name}</span>
          <span className="block text-xs text-gray-500">{row.vehicle_registration}</span>
        </span>
      ),
      exportValue: (row) => `${row.vehicle_name} ${row.vehicle_registration}`,
    },
    { header: "Job", cell: (row) => row.title, exportValue: (row) => row.title, className: "min-w-[12rem] whitespace-normal text-gray-700" },
    { header: "Kind", cell: (row) => labelFrom(MAINTENANCE_TYPES, row.maintenance_type), exportValue: (row) => labelFrom(MAINTENANCE_TYPES, row.maintenance_type), className: "text-gray-700" },
    { header: "Priority", cell: (row) => <StatusDot label={labelFrom(PRIORITIES, row.priority)} dot={priorityDot(row.priority)} />, exportValue: (row) => labelFrom(PRIORITIES, row.priority) },
    { header: "Cost", cell: (row) => money(row.actual_cost ?? row.estimated_cost) || <span className="text-gray-300">—</span>, exportValue: (row) => Number(row.actual_cost ?? row.estimated_cost ?? 0), align: "right" },
    { header: "Asked by", cell: (row) => row.requested_by_name, exportValue: (row) => row.requested_by_name, exportOnly: true },
    { header: "Status", cell: (row) => <StatusDot {...maintenanceStatus(row)} />, exportValue: (row) => maintenanceStatus(row).label },
  ];

  const job = viewing !== null ? jobs?.find((row) => row.id === viewing) : undefined;
  const openTrips = job ? trips.filter((row) => row.vehicle === job.vehicle && row.trip_date >= today && (row.status === "scheduled" || row.status === "in_progress")).length : 0;

  return (
    <div className="space-y-4">
      <Notice notice={notice} onDismiss={() => setNotice(null)} />
      <ClientRegister
        key={initialView}
        initialState={initialView ? { view: initialView } : undefined}
        title="Maintenance"
        description={canManage ? "Open a job to send the vehicle to the workshop, close it or cancel it." : "Repairs and services you have asked for."}
        actions={
          <button type="button" onClick={() => setRequesting(true)} className="btn-primary h-8 min-h-8 px-3 text-xs">
            <Wrench className="mr-1.5 h-3.5 w-3.5" /> Ask for a repair
          </button>
        }
        rows={jobs}
        config={maintenanceConfig}
        columns={columns}
        rowKey={(row) => row.id}
        onOpen={(row) => {
          start.reset();
          cancel.reset();
          setViewing(row.id);
        }}
        openLabel={(row) => (canManage && row.status === "pending" ? "Book in" : canManage && row.status === "in_progress" ? "Close" : "Open")}
        noun={["job", "jobs"]}
        exportName={{ file: "transport-maintenance", title: "Vehicle Maintenance" }}
        isLoading={isLoading}
        isError={isError}
        onRetry={onRetry}
        emptyText="No repairs or services asked for."
      />

      {job && !completing && (
        <DetailsModal
          title={`${job.vehicle_name} · ${job.title}`}
          headline={{ label: labelFrom(MAINTENANCE_TYPES, job.maintenance_type), value: labelFrom(PRIORITIES, job.priority), note: `Asked for on ${day(job.requested_date)} by ${job.requested_by_name}` }}
          status={maintenanceStatus(job)}
          facts={[
            ["Vehicle", `${job.vehicle_name} (${job.vehicle_registration})`],
            ["Estimated cost", money(job.estimated_cost) || undefined],
            ["Booked in", day(job.scheduled_date) || undefined],
            ["With", job.assigned_to_name],
            ["Closed", job.completed_date ? `${day(job.completed_date)} by ${job.completed_by_name}` : undefined],
            ["Cost", money(job.actual_cost) || undefined],
            ["Hours", job.labor_hours ? String(Number(job.labor_hours)) : undefined],
            ["Checked and safe", job.status === "completed" ? (job.quality_check ? "Yes" : "Not checked") : undefined],
            ["Next service", day(job.next_maintenance_due) || undefined],
            ["Cancelled", job.cancelled_date ? `${day(job.cancelled_date)} by ${job.cancelled_by_name}` : undefined],
          ]}
          sections={[
            { title: "What is wrong", body: job.description },
            { title: "Work done", body: job.work_performed },
            { title: "Parts used", body: job.parts_used },
            { title: "Check notes", body: job.quality_notes },
            { title: "Why it was cancelled", body: job.cancellation_reason, tone: "danger" },
          ]}
          onClose={() => setViewing(null)}
          footer={
            <>
              <button type="button" className="btn btn-outline" onClick={() => setViewing(null)}>Close</button>
              {canManage && job.status === "in_progress" && (
                <button type="button" onClick={() => setCompleting(true)} className="btn btn-primary gap-1.5">
                  <CheckCircle2 className="h-4 w-4" /> Back from the workshop
                </button>
              )}
            </>
          }
        >
          {canManage && job.status === "pending" && (
            <ActionPanel
              title="Send it to the workshop"
              description={openTrips ? `It has ${openTrips} open ${openTrips === 1 ? "trip" : "trips"} from today; call them off or finish them first.` : "It is taken off the road until the job is closed."}
            >
              <form
                onSubmit={(event: FormEvent<HTMLFormElement>) => {
                  event.preventDefault();
                  const date = String(new FormData(event.currentTarget).get("date") ?? "");
                  start.mutate({ id: job.id, scheduled_date: date || undefined });
                }}
                className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-end"
              >
                <Field label="Booked in for">
                  <input type="date" name="date" min={today} defaultValue={today} className="input mt-1" />
                </Field>
                <button disabled={start.isPending || openTrips > 0} className="btn btn-primary gap-1.5">
                  <Play className="h-4 w-4" /> {start.isPending ? "Sending…" : "Send it in"}
                </button>
              </form>
              {start.isError && errorLine(start.error, "The job could not be started.")}
            </ActionPanel>
          )}
          {canManage && isOpenJob(job) && (
            <CancelPanel
              title="Cancel the job"
              description="The vehicle goes back on the road without the work."
              placeholder="e.g. The mechanic found nothing wrong."
              context={{ key: "transport.note", params: { kind: "cancel", subject: "maintenance", id: job.id } }}
              button="Cancel the job"
              busy={cancel.isPending}
              error={cancel.error}
              errorText="The job could not be cancelled."
              onCancel={(reason) => cancel.mutate({ id: job.id, reason })}
            />
          )}
        </DetailsModal>
      )}

      {job && completing && (
        <CompleteForm
          job={job}
          onClose={() => setCompleting(false)}
          onDone={(saved) => {
            setCompleting(false);
            done(`${saved.vehicle_name} is back on the road.`, true);
          }}
        />
      )}
      {requesting && (
        <RequestForm
          vehicles={vehicles}
          busyVehicles={busyVehicles}
          onClose={() => setRequesting(false)}
          onDone={(saved) => {
            setRequesting(false);
            done(`Request sent for ${saved.vehicle_name}.`);
            setViewing(saved.id);
          }}
        />
      )}
    </div>
  );
}
